/** Reducer-based state machine for the CommandBar AI conversation flow. */
import { useReducer, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateChat, sendChatMessage } from '@/shared/api/hooks/useChat';
import { jobKeys } from '@/shared/api/hooks';
import type { ChatEvent } from '@/shared/api/hooks/useChat';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export interface PendingSession {
  stepVersionIds: string[];
  lessonIds: string[];
  stepCount: number;
}

export interface PendingJob {
  jobId: string;
}

export interface ActiveToolCall {
  id: string;
  name: string;
}

// ─── State ────────────────────────────────────────────────────────────────────

interface ChatState {
  chatId: string | null;
  messages: ChatMessage[];
  isStreaming: boolean;
  pendingSession: PendingSession | null;
  pendingJob: PendingJob | null;
  suggestions: string[];
  suggestedPlaceholder: string | null;
  msgCounter: number;
  activeToolCalls: ActiveToolCall[];
  toolCounter: number;
}

type ChatAction =
  | { type: 'SET_CHAT_ID'; chatId: string }
  | { type: 'ADD_USER_MESSAGE'; id: string; content: string }
  | { type: 'ADD_ASSISTANT_MESSAGE'; id: string }
  | { type: 'APPEND_TOKEN'; content: string }
  /**
   * Wipe any tokens accumulated so far on the current assistant message.
   * Fired between tool rounds — the LLM sometimes emits preamble text in a
   * round that also calls tools, and we don't want that preamble to sit in
   * the reply when the post-tool round produces the real answer.
   */
  | { type: 'RESET_ASSISTANT_STREAM' }
  | { type: 'TOOL_START'; name: string }
  | { type: 'TOOL_END'; name: string }
  | { type: 'SET_PENDING_SESSION'; session: PendingSession }
  | { type: 'SET_PENDING_JOB'; job: PendingJob }
  | { type: 'SET_SUGGESTIONS'; items: string[]; placeholder: string | null }
  | { type: 'STREAM_DONE' }
  | { type: 'STREAM_ERROR' }
  | { type: 'CLEAR' };

const initialState: ChatState = {
  chatId: null,
  messages: [],
  isStreaming: false,
  pendingSession: null,
  pendingJob: null,
  suggestions: [],
  suggestedPlaceholder: null,
  msgCounter: 0,
  activeToolCalls: [],
  toolCounter: 0,
};

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_CHAT_ID':
      return { ...state, chatId: action.chatId };

    case 'ADD_USER_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, { id: action.id, role: 'user', content: action.content }],
        pendingSession: null,
        suggestions: [],
        suggestedPlaceholder: null,
        msgCounter: state.msgCounter + 1,
        activeToolCalls: [],
      };

    case 'ADD_ASSISTANT_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, { id: action.id, role: 'assistant', content: '' }],
        isStreaming: true,
        msgCounter: state.msgCounter + 1,
      };

    case 'APPEND_TOKEN': {
      const msgs = [...state.messages];
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg?.role === 'assistant') {
        msgs[msgs.length - 1] = { ...lastMsg, content: action.content };
      }
      return { ...state, messages: msgs };
    }

    case 'RESET_ASSISTANT_STREAM': {
      const msgs = [...state.messages];
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg?.role === 'assistant') {
        msgs[msgs.length - 1] = { ...lastMsg, content: '' };
      }
      return { ...state, messages: msgs };
    }

    case 'TOOL_START':
      return {
        ...state,
        activeToolCalls: [
          ...state.activeToolCalls,
          { id: `tool-${state.toolCounter + 1}`, name: action.name },
        ],
        toolCounter: state.toolCounter + 1,
      };

    case 'TOOL_END': {
      const idx = state.activeToolCalls.findIndex((t) => t.name === action.name);
      if (idx === -1) return state;
      const next = [...state.activeToolCalls];
      next.splice(idx, 1);
      return { ...state, activeToolCalls: next };
    }

    case 'SET_PENDING_SESSION':
      return { ...state, pendingSession: action.session };

    case 'SET_PENDING_JOB':
      return { ...state, pendingJob: action.job };

    case 'SET_SUGGESTIONS':
      return { ...state, suggestions: action.items, suggestedPlaceholder: action.placeholder };

    case 'STREAM_DONE':
      return { ...state, isStreaming: false, activeToolCalls: [] };

    case 'STREAM_ERROR':
      return { ...state, isStreaming: false, activeToolCalls: [] };

    case 'CLEAR':
      return initialState;

    default:
      return state;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCommandBarChat() {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const queryClient = useQueryClient();
  const createChat = useCreateChat();
  const streamingContentRef = useRef('');

  const handleStreamEvent = useCallback(
    (event: ChatEvent) => {
      switch (event.type) {
        case 'token':
          streamingContentRef.current += event.content;
          dispatch({ type: 'APPEND_TOKEN', content: streamingContentRef.current });
          break;

        case 'reset_stream':
          // Backend signal that the current round had tool calls and any
          // preamble tokens already streamed are about to be superseded by
          // fresh output from the post-tool round. Wipe the buffer so the
          // new tokens don't concatenate onto the discarded preamble.
          streamingContentRef.current = '';
          dispatch({ type: 'RESET_ASSISTANT_STREAM' });
          break;

        case 'tool_start':
          dispatch({ type: 'TOOL_START', name: event.name });
          break;

        case 'tool_end':
          dispatch({ type: 'TOOL_END', name: event.name });
          break;

        case 'session_ready':
          dispatch({
            type: 'SET_PENDING_SESSION',
            session: {
              stepVersionIds: event.stepVersionIds,
              lessonIds: event.lessonIds,
              stepCount: event.stepCount,
            },
          });
          break;

        case 'job_created':
          dispatch({ type: 'SET_PENDING_JOB', job: { jobId: event.jobId } });
          // Fire immediate loading toast and kick the job poll into 3s mode
          toast.loading('Planning your lesson...', { id: event.jobId, duration: Infinity });
          void queryClient.invalidateQueries({ queryKey: jobKeys.all });
          break;

        case 'suggestions':
          dispatch({ type: 'SET_SUGGESTIONS', items: event.items, placeholder: event.placeholder });
          break;

        case 'done':
          dispatch({ type: 'STREAM_DONE' });
          break;

        case 'error':
          dispatch({ type: 'STREAM_ERROR' });
          toast.error(event.message);
          break;
      }
    },
    [queryClient]
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || state.isStreaming) return;

      // Show user message immediately
      const userId = `msg-${state.msgCounter + 1}`;
      dispatch({ type: 'ADD_USER_MESSAGE', id: userId, content: trimmed });

      // Brief pause before showing "thinking..." — mimics natural conversation rhythm
      await new Promise((r) => setTimeout(r, 600));

      const assistantId = `msg-${state.msgCounter + 2}`;
      streamingContentRef.current = '';
      dispatch({ type: 'ADD_ASSISTANT_MESSAGE', id: assistantId });

      let chatId = state.chatId;
      if (!chatId) {
        try {
          const newChat = await createChat.mutateAsync();
          chatId = newChat.id;
          dispatch({ type: 'SET_CHAT_ID', chatId });
        } catch (err: unknown) {
          dispatch({ type: 'STREAM_ERROR' });
          const message = err instanceof Error ? err.message : 'Failed to create chat';
          toast.error(message);
          return;
        }
      }

      try {
        await sendChatMessage(chatId, trimmed, handleStreamEvent);
      } catch (err: unknown) {
        dispatch({ type: 'STREAM_ERROR' });
        const message = err instanceof Error ? err.message : 'Failed to send message';
        toast.error(message);
      }
    },
    [state.isStreaming, state.chatId, state.msgCounter, createChat, handleStreamEvent]
  );

  const clear = useCallback(() => {
    dispatch({ type: 'CLEAR' });
    streamingContentRef.current = '';
  }, []);

  return {
    ...state,
    send,
    clear,
  };
}

export type CommandBarChatResult = ReturnType<typeof useCommandBarChat>;
